import { processShowListingResponse, clearStructuredData } from './analyticsHelpers';

export const debounce = (fn, ms = 350) => {
	let t; return (...a) => {
		clearTimeout(t); t = setTimeout(() => fn(...a), ms);
	};
};
export const has3Chars = (s) => ((s || '').replace(/\s+/g, '').length > 2);

const $ = (root, sel) => root.querySelector(sel);
const $$ = (root, sel) => Array.from(root.querySelectorAll(sel));

export function toQS(obj) {
	const u = new URLSearchParams();
	Object.entries(obj).forEach(([k, v]) => {
		if (Array.isArray(v)) {
			const uniq = [...new Set(v)].filter(Boolean);
			if (uniq.length) {
				u.set(k, uniq.join(','));
			}
		} else if (v !== '' && v !== null) {
			u.set(k, v);
		}
	});
	return u.toString().replace(/%2C/gi, ',');
}
export function cleanParams(obj, defaults = {}) {
	const out = {};
	for (const k in obj) {
		const v = obj[ k ];
		const val = Array.isArray(v) ? v.filter(Boolean) : v;
		if (Array.isArray(val) ? val.length : (val !== '' && val != null)) {
			out[ k ] = val;
		}
	}
	for (const k in defaults) {
		if (k in out && !Array.isArray(out[ k ]) && String(out[ k ]) === String(defaults[ k ])) {
			delete out[ k ];
		}
	}
	return out;
}
export function getInlineObj(form, multiKeys = new Set()) {
	const fd = new FormData(form);
	const out = {};
	for (const [rawK, v] of fd) {
		const k = rawK.replace(/\[\]$/, '');
		if (!multiKeys.has(k)) {
			out[ k ] = v;
		} else {
			(out[ k ] ||= []).push(v);
		}
	}
	return out;
}
export function paintChoiceActives(root) {
	const scope = (root || document);
	scope.querySelectorAll('label.choice > input, label.chip > input').forEach((inp) => {
		inp.closest('label.choice, label.chip')?.classList.toggle('is-active', inp.checked);
	});
	// radio groups
	const seen = new Set();
	scope.querySelectorAll('label.choice > input[type="radio"], label.chip > input[type="radio"]').forEach((r) => {
		if (!r.name || seen.has(r.name)) {
			return;
		}
		seen.add(r.name);
		scope.querySelectorAll(`input[type="radio"][name="${ CSS.escape(r.name) }"]`).forEach((g) => {
			g.closest('label.choice, label.chip')?.classList.toggle('is-active', g.checked);
		});
	});
}

/*
 * Toggles a class on each event card to hide the "Save up to X%" / exclusive
 * pricing badge once the visitor's selected date range falls outside the
 * period the card's data-offer-start / data-offer-end attributes cover.
 *
 * @param {Element|Document} root Scope to search within (defaults to document).
 * @param {string} from Selected start date, 'Y-m-d', or '' for no lower bound.
 * @param {string} to Selected end date, 'Y-m-d', or '' for no upper bound.
 */
export function applyOfferDateVisibility(root, from, to) {
	const scope = (root || document);
	scope.querySelectorAll('[data-offer-start]').forEach((card) => {
		const offerStart = card.getAttribute('data-offer-start') || '';
		const offerEnd = card.getAttribute('data-offer-end') || '';

		// No recorded offer window: nothing to bound, leave as-is.
		if (!offerStart || !offerEnd) {
			card.classList.remove('shows-grid-item--offer-out-of-range');
			return;
		}

		// Y-m-d strings compare correctly with standard string comparison.
		const outOfRange = (from && offerEnd < from) || (to && offerStart > to);
		card.classList.toggle('shows-grid-item--offer-out-of-range', !!outOfRange);
	});
}

function scopeButtons(scope) {
	return { applyBtn: $(scope, '[data-submit]'), cancelBtn: $(scope, '[data-cancel]') };
}
function scopeHasAnySelection(scope) {
	const anyChecked = !!$(scope, 'input[type="checkbox"]:checked, input[type="radio"]:checked');
	const filled = $$(scope, 'input[type="text"], input[type="search"], input[type="number"]').some((i) => (i.value || '').trim() !== '');
	// Single-thumb max-only slider; min axis removed across all filter bars.
	const max = $(scope, 'input[name="max"]')?.value ?? '';
	const anyPrice = max !== '' && Number(max) < 100;
	const anyDate = ($(scope, 'input[name="from"]')?.value || $(scope, 'input[name="to"]')?.value) ? true : false;
	return anyChecked || filled || anyPrice || anyDate;
}
export function markScopeDirty(scope, isDirty) {
	if (!scope) {
		return;
	}
	scope.dataset.dirty = isDirty ? '1' : '';
	const { applyBtn, cancelBtn } = scopeButtons(scope);
	if (applyBtn) {
		applyBtn.disabled = !isDirty && !scopeHasAnySelection(scope);
	}
	if (cancelBtn) {
		if (scope.matches('dialog')) {
			cancelBtn.disabled = false; cancelBtn.textContent = 'Cancel';
			cancelBtn.setAttribute('data-cancel-mode', 'cancel');
		} else {
			const hasSel = scopeHasAnySelection(scope);
			cancelBtn.disabled = false;
			cancelBtn.textContent = hasSel ? 'Clear' : 'Cancel';
			cancelBtn.setAttribute('data-cancel-mode', hasSel ? 'clear' : 'cancel');
		}
	}
}
export function primeScopeState(scope) {
	markScopeDirty(scope, false); paintChoiceActives(scope);
}

/* ---------- Price slider ---------- */
export function bindPriceControls(root) {
	const wrap = root?.querySelector?.('[data-price-wrap]');
	if (!wrap || wrap.dataset.bound) {
		return;
	}

	// Single-thumb max-only slider. The min/lower-bound axis was removed across
	// all filter bars (no observed use case for "tickets above £X", only
	// "tickets up to £X"), so this widget tracks only an upper threshold. The
	// markup no longer carries <input name="min">; min is implicit 0 and never
	// emitted to the form or REST query string. STEP=10 snaps URL values to a
	// dozen-ish discrete buckets, materially shrinking Varnish cache fan-out.
	const hiddenMax = wrap.querySelector('input[name="max"]');
	if (!hiddenMax) {
		// Defensive: nothing to bind to. The markup should always include the
		// max input via _price.php / the inline modal copies.
		return;
	}
	const labMax = wrap.querySelector('[data-price-max-label]');
	const legendsRow = wrap.querySelector('[data-range-track]');

	const ui = document.createElement('div'); ui.className = 'range';
	const track = document.createElement('div'); track.className = 'range__track';
	const fill = document.createElement('div'); fill.className = 'range__fill';
	const hMax = document.createElement('button'); hMax.type = 'button'; hMax.className = 'range__handle range__handle--max'; hMax.setAttribute('role', 'slider'); hMax.setAttribute('aria-label', 'Maximum price');
	track.append(fill, hMax); ui.append(track); legendsRow.after(ui);

	const MIN = 0,
		MAX = 101,    // "100+" / unlimited sentinel
		FLOOR = 10,   // minimum useful filter value — guards against an accidental
		              // leftmost-tap producing &max=0 (empty result set, no obvious recovery)
		STEP = 10;
	// Snap to discrete URL buckets {10, 20, ..., 90, unlimited}. Any snapped
	// value of 100+ collapses to the unlimited sentinel rather than rendering
	// as "exactly £100" — that distinction has no editorial meaning and the
	// rightmost track positions need to reach the "100+" state reliably.
	const clamp = (v) => {
		const rounded = Math.round(v / STEP) * STEP;
		if (rounded >= 100) {
			return MAX;
		}
		return Math.max(FLOOR, rounded);
	};
	const toPct = (v) => (v / MAX) * 100;

	let valMax = (hiddenMax.value === '' || !Number.isFinite(+hiddenMax.value)) ? MAX : clamp(+hiddenMax.value);

	const apply = () => {
		const pctMax = toPct(valMax);
		hMax.style.left = `${ pctMax }%`;
		fill.style.left = '0%';
		fill.style.width = `${ pctMax }%`;
		hiddenMax.value = (valMax >= 101) ? '' : String(valMax);

		hMax.setAttribute('aria-valuenow', String(valMax >= 101 ? 100 : valMax));
		hMax.setAttribute('aria-valuemin', '0');
		hMax.setAttribute('aria-valuemax', '100');

		if (labMax) {
			labMax.textContent = (valMax >= 101) ? '100+' : String(valMax);
		}
	};

	// Allow external reset to 0–100+
	wrap.resetPriceUI = () => {
		valMax = 101;
		apply();
	};

	const rect = () => track.getBoundingClientRect();
	const valueFromX = (x) => {
		const r = rect();
		const ratio = Math.max(0, Math.min(1, (x - r.left) / r.width));
		return clamp(MIN + ratio * (MAX - MIN));
	};

	let dragging = false;
	let activeListeners = [];

	const cleanup = () => {
		// Remove all active listeners
		activeListeners.forEach(({ target, type, handler }) => {
			target.removeEventListener(type, handler);
		});
		activeListeners = [];

		// Re-enable text selection
		document.body.style.userSelect = '';
		document.body.style.webkitUserSelect = '';
		document.body.style.msUserSelect = '';
	};

	const addListener = (target, type, handler, options) => {
		target.addEventListener(type, handler, options);
		activeListeners.push({ target, type, handler });
	};

	const updateValue = (clientX) => {
		if (!dragging) {
			return;
		}
		valMax = Math.max(valueFromX(clientX), MIN);
		apply();
	};

	const startDrag = (ev) => {
		if (ev.preventDefault) {
			ev.preventDefault();
		}
		if (ev.stopPropagation) {
			ev.stopPropagation();
		}

		dragging = true;

		document.body.style.userSelect = 'none';
		document.body.style.webkitUserSelect = 'none';
		document.body.style.msUserSelect = 'none';

		const moveHandler = (e) => {
			if (!dragging) {
				return;
			}

			if (e.preventDefault) {
				e.preventDefault();
			}

			let clientX;
			if (e.touches && e.touches[ 0 ]) {
				clientX = e.touches[ 0 ].clientX;
			} else if (e.clientX !== undefined) {
				clientX = e.clientX;
			} else {
				return;
			}

			updateValue(clientX);
		};

		const endHandler = (e) => {
			if (!dragging) {
				return;
			}

			if (e.preventDefault) {
				e.preventDefault();
			}

			dragging = false;

			const s = wrap.closest('dialog, .popover__panel');
			if (s) {
				markScopeDirty(s, true);
			}

			cleanup();
		};

		const hasPointerEvents = window.PointerEvent;

		if (hasPointerEvents) {
			addListener(document, 'pointermove', moveHandler, { passive: false });
			addListener(document, 'pointerup', endHandler);
			addListener(document, 'pointercancel', endHandler);
		} else {
			addListener(document, 'mousemove', moveHandler, { passive: false });
			addListener(document, 'touchmove', moveHandler, { passive: false });
			addListener(document, 'mouseup', endHandler);
			addListener(document, 'touchend', endHandler);
			addListener(document, 'touchcancel', endHandler);
		}

		const escapeHandler = (e) => {
			if (e.key === 'Escape' && dragging) {
				endHandler(e);
			}
		};
		addListener(document, 'keydown', escapeHandler);
	};

	const hasPointerEvents = window.PointerEvent;

	if (hasPointerEvents) {
		hMax.addEventListener('pointerdown', (ev) => {
			ev.preventDefault();
			startDrag(ev);
		});
	} else {
		hMax.addEventListener('mousedown', (ev) => startDrag(ev));
		hMax.addEventListener('touchstart', (ev) => startDrag(ev), { passive: false });
	}

	// Track click to jump to position
	track.addEventListener('click', (ev) => {
		if (dragging || ev.target === hMax) {
			return;
		}

		valMax = valueFromX(ev.clientX);
		apply();

		const s = wrap.closest('dialog, .popover__panel');
		if (s) {
			markScopeDirty(s, true);
		}
	});

	// Keyboard navigation
	const keyStep = (ev, cur) => {
		switch (ev.key) {
			case 'ArrowLeft':
			case 'ArrowDown':
				return cur - STEP;
			case 'ArrowRight':
			case 'ArrowUp':
				return cur + STEP;
			case 'PageDown':
				return cur - 10;
			case 'PageUp':
				return cur + 10;
			case 'Home':
				return MIN;
			case 'End':
				return MAX;
			default:
				return null;
		}
	};

	hMax.addEventListener('keydown', (ev) => {
		const n = keyStep(ev, valMax);
		if (n === null) {
			return;
		}

		ev.preventDefault();
		valMax = clamp(n);
		apply();

		const s = wrap.closest('dialog, .popover__panel');
		if (s) {
			markScopeDirty(s, true);
		}
	});

	// Initialise
	apply();
	wrap.dataset.bound = '1';

	if ('MutationObserver' in window) {
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				mutation.removedNodes.forEach((node) => {
					if (node === wrap || (node.contains && node.contains(wrap))) {
						cleanup();
						observer.disconnect();
					}
				});
			});
		});

		if (wrap.parentNode) {
			observer.observe(wrap.parentNode, { childList: true });
		}
	}
}

export function wireCalendarEnable(scope, container, fromInput, toInput, mountCalendar) {
	if (!container || container.dataset.calBound) {
		return;
	}
	const enable = () => markScopeDirty(scope, true);
	container.addEventListener('date-selected', enable);
	[fromInput, toInput].filter(Boolean).forEach((i) => {
		i.addEventListener('input', enable);
		i.addEventListener('change', enable);
		new MutationObserver(enable).observe(i, { attributes: true, attributeFilter: ['value'] });
	});
	mountCalendar();
	container.dataset.calBound = '1';
}

/* ---------- URL seeding / chips / counts ---------- */
export function seedFormFromURL(form, { multi = new Set(), defaults = {} } = {}) {
	const u = new URL(location.href); const p = u.searchParams;
	['q', 'from', 'to', 'max', 'sort', 'window', 'today', 'offers', 'advance_offers', 'coming_soon', 'last_chance', 'todays-offers'].forEach((k) => {
		const v = p.get(k);
		const inp = form.querySelector(`[name="${ k }"]`) || form.querySelector(`#${ k }`);
		if (!inp) {
			return;
		}
		if (inp.type === 'radio') {
			const targetVal = v ?? defaults[ k ] ?? '';
			if (targetVal) {
				const radio = form.querySelector(`input[type="radio"][name="${ k }"][value="${ CSS.escape(targetVal) }"]`);
				if (radio) {
					radio.checked = true;
				}
			}
		} else if (inp.type === 'hidden' || inp.tagName === 'INPUT' || inp.tagName === 'SELECT') {
			inp.value = (v ?? defaults[ k ] ?? '');
		}
	});
	multi.forEach((key) => {
		const raw = p.get(key); const vals = raw ? raw.split(',').map((s) => s.trim()).filter(Boolean) : [];
		const set = new Set(vals);
		form.querySelectorAll(`input[type="checkbox"][name="${ key }[]"], input[type="checkbox"][name="${ key }"]`).forEach((i) => {
			i.checked = set.has(i.value);
		});
		const hidden = form.querySelector(`input[type="hidden"][name="${ key }"]`); if (hidden) {
			hidden.value = vals.join(',');
		}
	});
	paintChoiceActives(form);
}
export function updateChipVisuals(bar, form) {
	const val = (name) => (form.querySelector(`input[name="${ name }"]`)?.value || '') === '1';
	['today', 'offers', 'coming_soon', 'last_chance', 'advance_offers'].forEach((key) => {
		bar.querySelectorAll(`[data-filter-toggle="${ key }"]`).forEach((b) => {
			b.classList.toggle('is-active', val(key));
			b.setAttribute('aria-pressed', val(key) ? 'true' : 'false');
		});
	});
}
/**
 * Updates the "Filter & Sort" button count and active state.
 *
 * @param {Element}  bar               - The filter bar root element.
 * @param {Element}  form              - The form element containing filter inputs.
 * @param {Object}   opts
 * @param {Set}      opts.multiCsvKeys - Keys whose hidden inputs hold CSV values.
 * @param {string[]} opts.toggleKeys   - Additional boolean toggle keys (e.g.
 *                                     coming_soon, last_chance, offers) that
 *                                     should increment the count when active.
 * @param            opts.perItemKeys
 */
export function updateAllFiltersCount(bar, form, { multiCsvKeys = new Set(['time', 'type', 'genre']), toggleKeys = [], perItemKeys = new Set() } = {}) {
	const isNonEmpty = (v) => Array.isArray(v) ? v.filter(Boolean).length > 0 : (v !== '' && v != null);
	const getVal = (name) => {
		const cbs = [
			...form.querySelectorAll(`input[type="checkbox"][name="${ name }"]`),
			...form.querySelectorAll(`input[type="checkbox"][name="${ name }[]"]`)
		];
		if (cbs.length) {
			return cbs.filter((i) => i.checked).map((i) => i.value);
		}

		const radios = form.querySelectorAll(`input[type="radio"][name="${ name }"]`);
		if (radios.length) {
			const checked = Array.from(radios).find((r) => r.checked);
			return checked ? checked.value : '';
		}
		const hidden = form.querySelector(`input[name="${ name }"]`);
		if (hidden && multiCsvKeys.has(name)) {
			return (hidden.value || '').split(',').filter(Boolean);
		}
		return form.querySelector(`[name="${ name }"]`)?.value ?? '';
	};
	let count = 0;
	const from = getVal('from'),
		to = getVal('to');
	if ((from && String(from).trim()) || (to && String(to).trim())) {
		count ++;
	}
	['time', 'type', 'genre', 'shows'].forEach((k) => {
		const val = getVal(k);
		if (perItemKeys.has(k) && Array.isArray(val)) {
			count += val.filter(Boolean).length;
		} else if (isNonEmpty(val)) {
			count ++;
		}
	});
	// Single-thumb max-only slider; min axis removed across all filter bars.
	const max = getVal('max');
	if (max !== '' && max !== null && Number(max) < 100) {
		count ++;
	}

	// Count boolean toggle filters that live inside the modal (e.g. coming_soon,
	// last_chance, offers). These are not checkbox/radio inputs so they are not
	// picked up by the standard loops above.
	toggleKeys.forEach((k) => {
		if (getVal(k) === '1') {
			count ++;
		}
	});

	const sortVal = getVal('sort');
	if (sortVal && sortVal !== 'name_asc' && sortVal !== 'name-atz') {
		count ++;
	}

	bar.querySelectorAll('[data-open="all"]').forEach((btn) => {
		// Mark the button active whenever any filter within the modal is set.
		btn.classList.toggle('is-active', count > 0);
		btn.setAttribute('aria-pressed', count > 0 ? 'true' : 'false');

		const labelEl = btn.querySelector('[data-all-count]');

		if (labelEl) {
			/*
			 * Two patterns exist for [data-all-count]:
			 *
			 * 1. Count-only badge: the element contains only "(N)" or is empty.
			 *    The label text lives in a sibling element and must not be
			 *    touched. Detected by stripping the count pattern and checking
			 *    whether anything remains.
			 *
			 * 2. Full-label element (legacy): the element contains both the
			 *    label text and the count. The base text is preserved and the
			 *    count is appended or removed.
			 */
			const stripped = labelEl.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();

			if (stripped === '') {
				labelEl.textContent = count ? `(${ count })` : '';
			} else {
				labelEl.textContent = count ? `${ stripped } (${ count })` : stripped;
			}
		} else {
			let textNode = null;
			btn.childNodes.forEach((node) => {
				if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
					textNode = node;
				}
			});

			if (textNode) {
				const base = textNode.textContent.trim().replace(/\s*\(\d+\)\s*$/, '');
				textNode.textContent = ` ${ count ? `${ base } (${ count })` : base }`;
			}
		}
	});
}
export function updateClearChip(bar, currentParams, defaults = {}, opts = {}) {
	const ignoreKeys = new Set(opts.ignoreKeys || []);
	const defaultsMulti = { ...(opts.defaultsMulti || {}) };
	const preserve = { ...(opts.preserve || {}) };

	if (bar) {
		if (bar.getAttribute('data-force-offers') === '1') {
			ignoreKeys.add('offers');
		}
		if (bar.getAttribute('data-force-today') === '1') {
			ignoreKeys.add('today');
		}
		ignoreKeys.add('list_base');

		if (bar.getAttribute('data-genre-locked') === '1') {
			ignoreKeys.add('genre');
			const defaultGenreCsv = (bar.getAttribute('data-default-genre') || '').trim();
			const defaultGenres = defaultGenreCsv ? defaultGenreCsv.split(',').map((s) => s.trim()).filter(Boolean) : [];
			defaultsMulti.genre = defaultGenres;
			preserve.genre = defaultGenres;
		}
	}

	const asSet = (arr) => new Set((Array.isArray(arr) ? arr : [arr]).filter(Boolean).map(String));
	const setsEqual = (a, b) => {
		const A = asSet(a);
		const B = asSet(b);
		if (A.size !== B.size) {
			return false;
		}
		for (const v of A) {
			if (!B.has(v)) {
				return false;
			}
		}
		return true;
	};

	const cleaned = {};
	Object.entries(currentParams || {}).forEach(([k, v]) => {
		const nonEmpty = Array.isArray(v) ? v.filter(Boolean).length > 0 : (v !== '' && v != null);
		if (!nonEmpty || ignoreKeys.has(k)) {
			return;
		}
		cleaned[ k ] = v;
	});

	Object.entries(defaults || {}).forEach(([k, v]) => {
		if (k in cleaned && !Array.isArray(cleaned[ k ]) && String(cleaned[ k ]) === String(v)) {
			delete cleaned[ k ];
		}
	});

	Object.entries(defaultsMulti).forEach(([k, vDefault]) => {
		if (k in cleaned && Array.isArray(cleaned[ k ]) && setsEqual(cleaned[ k ], vDefault)) {
			delete cleaned[ k ];
		}
	});

	if ('sort' in cleaned) {
		const map = {
			'name-atz': 'name_asc',
			'name-zta': 'name_desc',
			'date-old': 'date_old',
			'date-new': 'date_new'
		};
		const norm = map[ cleaned.sort ] || cleaned.sort || 'name_asc';
		if (norm === 'name_asc') {
			delete cleaned.sort;
		}
	}

	const hasRealFilters = Object.keys(cleaned).length > 0;

	bar?.classList.toggle('has-clear-chip', hasRealFilters);
	bar?.classList.toggle('no-clear-chip', !hasRealFilters);

	const base = new URL(location.pathname, location.origin);
	const qs = new URLSearchParams();
	Object.entries(preserve).forEach(([k, v]) => {
		if (Array.isArray(v)) {
			const csv = v.filter(Boolean).join(',');
			if (csv) {
				qs.set(k, csv);
			}
		} else if (v !== '' && v != null) {
			qs.set(k, String(v));
		}
	});
	const clearHref = qs.toString() ? `${ base.pathname }?${ qs }` : base.pathname;

	bar.querySelectorAll('[data-clear-chip]').forEach((wrap) => {
		wrap.style.display = hasRealFilters ? '' : 'none';
		const link = wrap.querySelector('[data-clear-chip-link]');
		if (link) {
			link.setAttribute('href', clearHref);
		}
	});
}
/**
 * Returns the navigation URL for the first [data-modal-goto] button that
 * carries the is-active class within a given dialog element.
 *
 * Called by modal Apply handlers to determine whether the user has selected
 * a navigation shortcut (e.g. Special Offers, Today's Tickets) rather than
 * confirming an inline filter change. Returns null when no goto button is
 * active so the Apply handler can fall through to normal filter application.
 *
 * @param {Element} modal - The dialog element to search within.
 * @return {string|null} The target URL, or null if none are active.
 */
export function resolveModalGoto(modal) {
	if (!modal) {
		return null;
	}
	const btn = Array.from(modal.querySelectorAll('[data-modal-goto]'))
		.find((b) => b.classList.contains('is-active') && b.dataset.modalGoto);
	if (!btn) {
		return null;
	}
	const url = btn.dataset.modalGoto;
	if (!/^(\/|https?:)/.test(url)) {
		return null;
	}
	return url;
}
/**
 * Detects if a popover panel is overflowing the right side of the viewport
 * and toggles a class to flip its alignment.
 *
 * @param panel
 */
export function adjustPopoverPosition(panel) {
	if (!panel) {
		return;
	}

	panel.classList.remove('popover__panel--right');

	requestAnimationFrame(() => {
		const rect = panel.getBoundingClientRect();
		const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

		// Also get the bounding box of the scrolling container
		const container = panel.closest('.site-tickets-filters__controls');
		const containerRect = container ? container.getBoundingClientRect() : null;

		// Check if it hits the edge of the viewport OR the edge of the scroll container
		const isOffScreen = rect.right > viewportWidth;
		const isClippedByContainer = containerRect && (rect.right > containerRect.right);

		if (isOffScreen || isClippedByContainer) {
			panel.classList.add('popover__panel--right');
		}
	});
}

/*
 * Reattaches any query parameters the filter bar does not own to a freshly
 * built filter URL before it is written to the address bar.
 *
 * The renderer rebuilds the querystring from the active filters only, so a
 * plain history.pushState of that string would discard every other parameter
 * currently on the URL. Marketing attribution and click identifiers such as
 * utm_source, utm_medium, utm_campaign, gclid, fbclid and msclkid arrive on the
 * landing URL and must remain there for analytics and advertising pixels to read
 * on later interactions. Those parameters are not filter inputs, so they are
 * absent from the rebuilt string and would otherwise be lost the moment a
 * visitor touches any filter.
 *
 * The set of managed keys is derived from the form controls, the configured
 * defaults and the free-text search key, so it always matches exactly what the
 * renderer emits. Any parameter on the current URL whose key falls outside that
 * set is treated as foreign and carried across untouched. The filter portion of
 * the URL is left byte-for-byte intact so its existing formatting (for example
 * comma-separated multi-value keys) is preserved.
 *
 * @param {string}  filterUrl The path and filter-only querystring to write.
 * @param {Element} form      The filter form, used to derive the managed keys.
 * @param {Object}  defaults  The configured filter defaults.
 * @param {string}  searchKey The free-text search parameter name.
 * @return {string} The URL with foreign parameters reattached.
 */
export function mergeUnmanagedParams(filterUrl, form, defaults = {}, searchKey = 'q') {
	const incoming = new URLSearchParams(location.search);

	// Nothing on the current URL to carry across.
	let hasIncoming = false;
	incoming.forEach(() => {
		hasIncoming = true;
	});
	if (!hasIncoming) {
		return filterUrl;
	}

	// Keys the filter bar is authoritative for. Pagination internals are listed
	// explicitly because they may appear on a URL without a matching form field.
	const managed = new Set([searchKey, 'limit', 'offset']);
	Object.keys(defaults || {}).forEach((k) => managed.add(k));
	form?.querySelectorAll('[name]').forEach((el) => {
		const name = el.getAttribute('name');
		if (name) {
			managed.add(name.replace(/\[\]$/, ''));
		}
	});

	// Collect foreign parameters as already-encoded fragments so the filter
	// querystring keeps its existing formatting untouched.
	const foreign = [];
	for (const [key, value] of incoming.entries()) {
		const baseKey = key.replace(/\[\]$/, '');
		if (managed.has(baseKey)) {
			continue;
		}
		foreign.push(`${ encodeURIComponent(key) }=${ encodeURIComponent(value) }`);
	}

	if (!foreign.length) {
		return filterUrl;
	}

	const separator = filterUrl.includes('?') ? '&' : '?';
	return `${ filterUrl }${ separator }${ foreign.join('&') }`;
}

/* ---------- renderer ---------- */
export function createRenderer({
	bar, form, list, endpoint, paramsFromForm,
	defaults = {}, searchKey = 'q', normaliseSort,
	pushHistory = true, onAfterRender, onAfterUiRefresh
}) {
	const cache = new Map();
	let inflight = null;
	const ssrLimit = list?.getAttribute('data-limit') || '';

	const seed = (qs, data) => {
		cache.set(qs, data);
	};

	const render = async (push = true) => {
		const base = cleanParams(paramsFromForm(), defaults);
		if (normaliseSort) {
			base.sort = normaliseSort(base.sort);
		}

		const qEl = form.querySelector(`input[name="${ searchKey }"], #${ searchKey }`);
		const qVal = (qEl?.value || '').trim();
		// If the show search module has flagged q as suppressed (because one or
		// more show tags are active), do not inject the typed value into params.
		// The show filter and free-text filter are mutually exclusive.
		const qSuppressed = qEl?.dataset?.qSuppressed === '1';
		if (!qSuppressed && has3Chars(qVal)) {
			base[ searchKey ] = qVal;
		} else {
			delete base[ searchKey ];
		}

		const qs = toQS(base);
		if (pushHistory && push) {
			// Build the filter-only URL, then reattach any tracking or attribution
			// parameters already on the address bar so they survive the rewrite.
			const filterUrl = qs ? `${ location.pathname }?${ qs }` : location.pathname;
			history.pushState({ qs }, '', mergeUnmanagedParams(filterUrl, form, defaults, searchKey));
		}

		// Include SSR limit in fetch so AJAX returns the full result set,
		// but keep it out of the browser URL.
		const fetchBase = ssrLimit && !base.limit ? { ...base, limit: ssrLimit } : base;
		const fetchQs = ssrLimit && !base.limit ? toQS(fetchBase) : qs;

		if (cache.has(fetchQs)) {
			const data = cache.get(fetchQs);
			if (list) {
				list.innerHTML = data?.html || '<p>No results.</p>';
			}
			// Process analytics data from cached response
			processShowListingResponse(data, { clearPrevious: true });
			onAfterRender?.(data, base); onAfterUiRefresh?.(base, qs);
			return;
		}

		if (inflight?.abort) {
			inflight.abort();
		}
		inflight = 'AbortController' in window ? new AbortController() : null;

		list?.setAttribute('aria-busy', 'true');
		try {
			const r = await fetch(`${ endpoint }${ fetchQs ? `?${ fetchQs }` : '' }`, { credentials: 'same-origin', signal: inflight?.signal });
			const data = await r.json();
			cache.set(fetchQs, data);
			if (list) {
				list.innerHTML = data?.html || '<p>No results.</p>';
			}
			// Process analytics data (datalayer & structured data)
			// Clear previous structured data since this is a new filter result
			processShowListingResponse(data, { clearPrevious: true });
			onAfterRender?.(data, base);
		} catch (e) {
			if (e.name !== 'AbortError' && list) {
				list.innerHTML = '<p>Sorry, something went wrong.</p>';
			}
			onAfterRender?.(null, null);
		} finally {
			list?.removeAttribute('aria-busy'); inflight = null;
			onAfterUiRefresh?.(base, qs);
		}
	};

	const q = form.querySelector(`input[name="${ searchKey }"], #${ searchKey }`);
	if (q) {
		// Do not re-render while the user is typing to add a show via the
		// typeahead. When show tags are active (qSuppressed = 1) the input
		// is being used to search for additional shows to select, not to
		// filter the listing. The listing should remain stable until a show
		// is confirmed or all tags are removed.
		const apply = debounce(() => {
			if (q.dataset.qSuppressed === '1') {
				return;
			}
			// Skip the network call entirely for partial queries (1–2 chars):
			// has3Chars() inside render() would strip `q` anyway, so the request
			// would refetch the unfiltered baseline on every keystroke. Empty
			// (length 0) must still render so clearing the box restores the
			// full list; 3+ chars renders with `q` included.
			const v = (q.value || '').trim();
			if (v.length > 0 && v.length < 3) {
				return;
			}
			render(true);
		}, 350);
		q.addEventListener('input', apply);
		q.addEventListener('change', apply);
	}

	window.addEventListener('popstate', () => render(false));
	render.seed = seed;
	return render;
}

/* ---------- master init ---------- */
export function initFilterBar(config) {
	const {
		bar, form, list, endpoint,
		defaults,
		clearDefaults = defaults,
		multi = new Set(),
		normaliseSort,
		onBeforeOpenScope,
		onAfterOpenScope,
		onChipToggle,
		paramsFromForm,
		onAfterRender,
		onAfterUiRefresh,
		toggleKeys = [],
		render: externalRender
	} = config;

	seedFormFromURL(form, { multi, defaults });
	updateChipVisuals(bar, form);
	updateAllFiltersCount(bar, form, { toggleKeys });

	try {
		const baseNow = cleanParams(paramsFromForm?.() || {}, defaults || {});
		updateClearChip(bar, baseNow, clearDefaults || defaults || {});
	} catch (_) {
		console.error('filter bar not updated');
	}

	let activePanel = null;

	// --- iOS Feature Detection Gate ---
	const isIOS = CSS.supports('-webkit-touch-callout', 'none') &&
		window.matchMedia('(hover: none) and (pointer: coarse) and (max-width: 1024px)').matches;

	const getControlsEl = () => bar.querySelector(
		'.site-tickets-filters__controls, .site-ticket-booth-filters__controls, .site-access-tickets-filters__controls'
	);

	const closeAllPanels = () => {
		if (activePanel) {
			activePanel.setAttribute('hidden', '');
			activePanel = null;
		}
		bar.querySelectorAll('.popover__panel').forEach((p) => {
			p.setAttribute('hidden', '');
		});

		// Only strip the class if the device is iOS
		if (isIOS) {
			getControlsEl()?.classList.remove('has-open-popover');
		}
	};

	// OPEN scope (modal or popover)
	bar.addEventListener('click', (e) => {
		const btn = e.target.closest('[data-open]'); if (!btn) {
			return;
		}
		if (btn.tagName === 'A') {
			e.preventDefault();
		}

		const key = btn.getAttribute('data-open');
		const modal = bar.querySelector(`[data-modal="${ key }"]`);
		const panel = bar.querySelector(`[data-panel="${ key }"]`);

		onBeforeOpenScope?.(key, { modal, panel });

		if (modal) {
			modal.showModal?.();
			primeScopeState(modal);
			bindPriceControls(modal);
			onAfterOpenScope?.(key, { modal, panel });
			return;
		}
		if (!panel) {
			return;
		}

		const open = !panel.hasAttribute('hidden');
		closeAllPanels();

		if (!open) {
			panel.removeAttribute('hidden');
			primeScopeState(panel);
			bindPriceControls(panel);
			activePanel = panel;

			// Only inject the clipping class if the device is iOS
			if (isIOS) {
				getControlsEl()?.classList.add('has-open-popover');
			}

			onAfterOpenScope?.(key, { modal, panel });
		}
	});

	// CLOSE popovers on outside click
	document.addEventListener('click', (e) => {
		if (bar.contains(e.target)) {
			return;
		}
		closeAllPanels();
	});

	// built-in APPLY/CANCEL
	bar.addEventListener('click', (e) => {
		const isSubmit = e.target.matches('[data-submit]');
		const isCancel = e.target.matches('[data-cancel]');
		if (!isSubmit && !isCancel) {
			return;
		}

		e.preventDefault();
		const scope = e.target.closest('dialog, .popover__panel');

		if (isSubmit) {
			if (scope?.classList.contains('popover__panel')) {
				scope.setAttribute('hidden', '');
				activePanel = null;
			}
			scope?.closest('dialog')?.close?.();
			(config.render || externalRender)?.();

			closeAllPanels();
			return;
		}

		const mode = e.target.getAttribute('data-cancel-mode') || 'cancel';
		if (mode === 'clear' && scope && !scope.matches('dialog')) {
			// clear inline scope inputs
			scope.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((i) => {
				i.checked = false; i.closest('label.choice, label.chip')?.classList.remove('is-active');
			});
			// clear price & dates in this scope if present
			scope.querySelectorAll('input[name="max"]').forEach((i) => i.value = '');
			scope.querySelectorAll('[data-price-wrap]').forEach((w) => w.resetPriceUI?.());
			scope.querySelectorAll('input[name="from"], input[name="to"]').forEach((i) => i.value = '');
			markScopeDirty(scope, true);
			return;
		}

		if (scope?.classList.contains('popover__panel')) {
			scope.setAttribute('hidden', '');
			activePanel = null;
		}
		scope?.closest('dialog')?.close?.();
		closeAllPanels();
	});

	// CHIP toggles (offers/today/coming_soon/last_chance) — caller decides render timing
	bar.addEventListener('click', (e) => {
		const chip = e.target.closest('[data-filter-toggle]'); if (!chip) {
			return;
		}
		if (chip.tagName === 'A') {
			e.preventDefault();
		}
		onChipToggle?.(chip);
	});

	// data-modal-goto buttons inside dialogs/panels: toggle is-active on click.
	// Multiple goto buttons within the same scope are mutually exclusive because
	// only one navigation target can be pending at a time. Navigation itself is
	// deferred until the user confirms with Apply; clicking the button alone does
	// not navigate. This handler lives in initFilterBar so every filter bar
	// inherits the behaviour without each caller needing its own listener.
	bar.addEventListener('click', (e) => {
		const btn = e.target.closest('[data-modal-goto]');
		if (!btn) {
			return;
		}
		e.preventDefault();
		const scope = btn.closest('dialog, .popover__panel') || bar;
		const willBeActive = !btn.classList.contains('is-active');

		// Deactivate all other goto buttons in the same scope before toggling
		// the clicked one, so only one can be selected at a time.
		scope.querySelectorAll('[data-modal-goto]').forEach((b) => {
			b.classList.remove('is-active');
			b.setAttribute('aria-pressed', 'false');
		});

		// Deactivate incompatible modal-toggle buttons (e.g. Coming Soon / Last Chance)
		// when a goto navigation button is selected.
		if (willBeActive) {
			['coming_soon', 'last_chance'].forEach((k) => {
				const tb = scope.querySelector(`[data-modal-toggle="${ k }"]`);
				if (tb) {
					tb.classList.remove('is-active');
					tb.setAttribute('aria-pressed', 'false');
				}
			});
		}

		btn.classList.toggle('is-active', willBeActive);
		btn.setAttribute('aria-pressed', willBeActive ? 'true' : 'false');

		if (!willBeActive) {
			btn.blur();
		}
		if (scope) {
			markScopeDirty(scope, true);
		}
	});

	// Visuals/dirty on changes
	bar.addEventListener('change', (e) => {
		const inp = e.target.closest('label.choice > input, label.chip > input, input[name="max"]');
		if (!inp) {
			return;
		}
		inp.closest('label.choice, label.chip')?.classList.toggle('is-active', inp.checked);
		if (inp.type === 'radio' && inp.name) {
			const scope = inp.closest('.popover__panel') || inp.closest('dialog') || bar;
			scope.querySelectorAll(`label > input[type="radio"][name="${ CSS.escape(inp.name) }"]`).forEach((r) => r.closest('label.choice, label.chip')?.classList.toggle('is-active', r.checked));
			if (scope && (scope.matches('dialog') || scope.classList.contains('popover__panel'))) {
				markScopeDirty(scope, true);
			}
			return;
		}
		const scope = inp.closest('dialog, .popover__panel'); if (scope) {
			markScopeDirty(scope, true);
		}
	});

	let render = externalRender || null;
	if (!render && endpoint) {
		render = createRenderer({
			bar, form, list, endpoint, defaults,
			paramsFromForm, normaliseSort, onAfterRender,
			onAfterUiRefresh: (base, qs) => {
				updateChipVisuals(bar, form);
				updateAllFiltersCount(bar, form, { toggleKeys });
				updateClearChip(bar, base, clearDefaults || defaults);
			}
		});
	}
	config.render = render;
	paintChoiceActives(bar);
	updateAllFiltersCount(bar, form, { toggleKeys });
	return render;
}
